import { React, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Modal, Spinner } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Delete from "../../assets/images/delete.svg";
import EditIc from "../../assets/images/ic-edit.svg";
import ApiConfig from "../../api/ApiConfig";
import {
  simplePostCall,
  getWithAuthCall,
  simpleDeleteCall,
} from "../../api/ApiServices";
import { notifySuccess, notifyError } from "../../sharedComponent/notify";
import { DateDDMMYYYY } from "../../sharedComponent/common";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import { isValidUrl } from "./ffHelpers";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

const emptyTarget = {
  target_name: "",
  endpoint_url: "",
  http_method: "POST",
  auth_type: "bearer",
  auth_token: "",
  auto_push: false,
  is_active: true,
};

const IntegrationTargets = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [targets, setTargets] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyTarget);
  const [showToken, setShowToken] = useState(false); // U-12
  const [isEdit, setIsEdit] = useState(false); // U-12

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteName, setDeleteName] = useState(null);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);

  useEffect(() => {
    getTargets();
    getLogs();
  }, []);

  const getTargets = () => {
    setLoading(true);
    getWithAuthCall(ApiConfig.FLEET_FINANCE_INTEGRATION_CONFIG_LIST)
      .then((data) => {
        if (data?.success || data?.result) setTargets(data?.data || []);
        else setTargets([]);
      })
      .catch((err) => {
        console.log("targets err", err);
        setTargets([]);
      })
      .finally(() => setLoading(false));
  };

  const getLogs = () => {
    setLogsLoading(true);
    simplePostCall(
      ApiConfig.FLEET_FINANCE_INTEGRATION_LOG_LIST,
      JSON.stringify({ page: 1, page_limit: 10 })
    )
      .then((data) => {
        if (data?.success || data?.result) setLogs(data?.data || []);
        else setLogs([]);
      })
      .catch((err) => {
        console.log("logs err", err);
        setLogs([]);
      })
      .finally(() => setLogsLoading(false));
  };

  const openAdd = () => {
    setIsEdit(false);
    setForm(emptyTarget);
    setShowToken(false);
    setShowForm(true);
  };

  // U-12: save upserts by target_name, so Edit reuses it with the name locked
  const openEdit = (tg) => {
    setIsEdit(true);
    setForm({
      target_name: tg.target_name || "",
      endpoint_url: tg.endpoint_url || "",
      http_method: tg.http_method || "POST",
      auth_type: tg.auth_type || "bearer",
      auth_token: tg.auth_token || "",
      auto_push: !!tg.auto_push,
      is_active: tg.is_active === undefined || tg.is_active === null ? true : !!tg.is_active,
    });
    setShowToken(false);
    setShowForm(true);
  };

  const changeForm = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // FF-11: endpoint must be a valid http(s) URL
    if (!isValidUrl(form.endpoint_url)) {
      notifyError(t("Enter a valid http(s) URL"));
      return;
    }
    setSaving(true);
    const body = JSON.stringify({
      target_name: form.target_name,
      endpoint_url: form.endpoint_url,
      http_method: form.http_method,
      auth_type: form.auth_type,
      auth_token: form.auth_token,
      auto_push: form.auto_push,
      is_active: form.is_active,
    });
    simplePostCall(ApiConfig.FLEET_FINANCE_INTEGRATION_CONFIG_SAVE, body)
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Integration target saved");
          setShowForm(false);
          getTargets();
        } else {
          notifyError(data?.message || data?.error || "Unable to save target");
        }
      })
      .catch((err) => {
        console.log("save target err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setSaving(false));
  };

  const handlePush = (targetName) => {
    simplePostCall(
      ApiConfig.FLEET_FINANCE_INTEGRATION_PUSH,
      JSON.stringify({ target_name: targetName, limit: 50 })
    )
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Ledger entries pushed");
          getLogs();
        } else {
          notifyError(data?.message || data?.error || "Unable to push entries");
        }
      })
      .catch((err) => {
        console.log("push err", err);
        notifyError("Something went wrong");
      });
  };

  const confirmDelete = (name) => {
    setDeleteName(name);
    setDeleteModal(true);
  };

  const handleDelete = () => {
    simpleDeleteCall(
      ApiConfig.FLEET_FINANCE_INTEGRATION_CONFIG_DELETE,
      JSON.stringify({ target_name: deleteName })
    )
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Target deleted");
          getTargets();
        } else {
          notifyError(data?.message || data?.error || "Unable to delete target");
        }
      })
      .catch((err) => {
        console.log("delete target err", err);
        notifyError("Something went wrong");
      })
      .finally(() => {
        setDeleteModal(false);
        setDeleteName(null);
      });
  };

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_Integration">
        {/* Targets */}
        <div className="main-master-wrapper">
          <div
            className="d-flex justify-content-between align-items-center flex-wrap"
            style={{
              borderBottom: "0.5px solid #f6efe9",
              paddingBottom: 12,
              marginBottom: 20,
              gap: 12,
            }}
          >
            <div className="vehicleHead">
              <p style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
                {t("Integration Targets")}
              </p>
            </div>
            <button className="cx-btn-2" onClick={openAdd}>
              + {t("Add Target")}
            </button>
          </div>
          {loading ? (
            <Loader />
          ) : (
            <div id="scroll_insideThe_Padding_tabel">
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Name")}</th>
                      <th>{t("Endpoint URL")}</th>
                      <th>{t("Method")}</th>
                      <th>{t("Auth Type")}</th>
                      <th>{t("Auto Push")}</th>
                      <th>{t("Active")}</th>
                      <th>{t("Action")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {targets && targets.length > 0 ? (
                      targets.map((tg, i) => (
                        <tr key={tg.target_name || i}>
                          <td>{tg.target_name}</td>
                          <td>{tg.endpoint_url}</td>
                          <td>{tg.http_method}</td>
                          <td>{tg.auth_type}</td>
                          <td>{tg.auto_push ? t("Yes") : t("No")}</td>
                          <td>{tg.is_active ? t("Yes") : t("No")}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <button
                                className="cx-btn-1"
                                style={{ padding: "2px 10px", marginRight: 8 }}
                                onClick={() => handlePush(tg.target_name)}
                              >
                                {t("Push")}
                              </button>
                              <img
                                src={EditIc}
                                alt="edit"
                                style={{ cursor: "pointer", marginRight: 8 }}
                                onClick={() => openEdit(tg)}
                              />
                              <img
                                src={Delete}
                                alt="delete"
                                style={{ cursor: "pointer" }}
                                onClick={() => confirmDelete(tg.target_name)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Logs */}
        <div className="main-master-wrapper" style={{ marginTop: 16 }}>
          <div
            className="d-flex justify-content-between align-items-center flex-wrap"
            style={{
              borderBottom: "0.5px solid #f6efe9",
              paddingBottom: 12,
              marginBottom: 20,
              gap: 12,
            }}
          >
            <div className="vehicleHead">
              <p style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
                {t("Integration Logs")}
              </p>
            </div>
            <button className="cx-btn-1" onClick={getLogs}>
              {t("Refresh")}
            </button>
          </div>
          {logsLoading ? (
            <Loader />
          ) : (
            <div id="scroll_insideThe_Padding_tabel">
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("ID")}</th>
                      <th>{t("Target")}</th>
                      <th>{t("Entry")}</th>
                      <th>{t("Status")}</th>
                      <th>{t("Message")}</th>
                      <th>{t("Date")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {logs && logs.length > 0 ? (
                      logs.map((lg, i) => (
                        <tr key={lg.log_id || i}>
                          <td>{lg.log_id || i + 1}</td>
                          <td>{lg.target_name}</td>
                          <td>{lg.entry_id || "-"}</td>
                          <td>{lg.status}</td>
                          <td>{lg.message || lg.response || "-"}</td>
                          <td>
                            {lg.created_at ? DateDDMMYYYY(lg.created_at) : "-"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add target modal */}
      <Modal
        show={showForm}
        onHide={() => setShowForm(false)}
        centered
        className="common-model"
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {isEdit ? t("Edit Integration Target") : t("Add Integration Target")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Target Name")} <span>&#42;</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  name="target_name"
                  value={form.target_name}
                  onChange={changeForm}
                  disabled={isEdit}
                  placeholder="erp"
                />
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("HTTP Method")}
                </Form.Label>
                <Form.Select
                  name="http_method"
                  value={form.http_method}
                  onChange={changeForm}
                >
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                </Form.Select>
              </div>
              <div className="col-md-12 form_input_main">
                <Form.Label className="common-labels">
                  {t("Endpoint URL")} <span>&#42;</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  name="endpoint_url"
                  value={form.endpoint_url}
                  onChange={changeForm}
                  placeholder="https://webhook.site/..."
                />
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Auth Type")}
                </Form.Label>
                <Form.Select
                  name="auth_type"
                  value={form.auth_type}
                  onChange={changeForm}
                >
                  <option value="none">none</option>
                  <option value="bearer">bearer</option>
                  <option value="basic">basic</option>
                  <option value="apikey">apikey</option>
                </Form.Select>
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Auth Token")}
                </Form.Label>
                {/* U-12: masked by default with a show/hide toggle */}
                <div className="d-flex align-items-center" style={{ gap: 6 }}>
                  <Form.Control
                    type={showToken ? "text" : "password"}
                    name="auth_token"
                    value={form.auth_token}
                    onChange={changeForm}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="cx-btn-1"
                    style={{ whiteSpace: "nowrap", padding: "6px 10px" }}
                    onClick={() => setShowToken((s) => !s)}
                    aria-label={showToken ? t("Hide token") : t("Show token")}
                  >
                    {showToken ? t("Hide") : t("Show")}
                  </button>
                </div>
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Check
                  type="checkbox"
                  id="auto_push"
                  name="auto_push"
                  checked={form.auto_push}
                  onChange={changeForm}
                  label={t("Auto Push")}
                  style={{ marginTop: 10 }}
                />
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Check
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={form.is_active}
                  onChange={changeForm}
                  label={t("Active")}
                  style={{ marginTop: 10 }}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="pop-up-modal-footer">
            <div className="btn-wrapper">
              <button
                type="button"
                className="cx-btn-1"
                onClick={() => setShowForm(false)}
              >
                {t("Cancel")}
              </button>
              <button type="submit" className="cx-btn-2" disabled={saving}>
                {saving ? <Spinner animation="border" size="sm" /> : t("Save")}
              </button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete modal */}
      <Modal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        centered
        className="common-model"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Delete Integration Target")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("Are you sure you want to delete this target")} ?
        </Modal.Body>
        <Modal.Footer className="pop-up-modal-footer">
          <div className="btn-wrapper">
            <button className="cx-btn-1" onClick={() => setDeleteModal(false)}>
              {t("Cancel")}
            </button>
            <button className="cx-btn-2" onClick={handleDelete}>
              {t("Yes")}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </motion.div>
  );
};

export default IntegrationTargets;
